import { InsurancesStatusEnum } from "@frontend/lib/graphql/generated-gql";
import { ResultAsync } from "neverthrow";
import { fromPromise } from "xstate";
import { z } from "zod";
import { getStateIdFromUrl } from "../../devtool/playwright-generator";
import { getKioskPrices } from "../../server-actions/pricing-actions";
import type { ActorWithMetadata } from "./send-sms.actor";

/**
 * Input Schema
 */
export const FetchPricingInputSchema = z.object({
  // Consumer data
  consumerId: z.string().optional().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string(), // MM-dd-yyyy format

  // Insurance data (optional - if not provided, only cash prices are fetched)
  insurances: z
    .array(
      z.object({
        payerId: z.string().optional(),
        memberId: z.string(),
        payer: z.string(),
        state: z.string(),
        id: z.string().optional(),
        status: z.nativeEnum(InsurancesStatusEnum).optional(),
        error: z.string().nullable().optional(),
      }),
    )
    .optional(),

  // HRT list for pricing
  hrtIds: z.array(z.number()),

  // Organization context
  orgId: z.number(),

  // User email for requests
  email: z.string().email().or(z.literal("")),

  // Whether insurance is verified (if true, fetch insured prices)
  insuranceVerified: z.boolean().optional().default(false),

  // Practice ID for pricing context
  practiceId: z.number(),

  isPendingPricesFetch: z.boolean().optional().default(false),
});

type FetchPricingInput = z.infer<typeof FetchPricingInputSchema>;

/**
 * Success Schema
 */
const FetchPricingSuccessSchema = z.object({
  // Pricing map: hrtId -> { CASH, INSURED }
  pricingMap: z.record(
    z.object({
      priceData: z.object({
        CASH: z
          .object({
            price: z.number(),
            pricedTreatmentId: z.number().or(z.string()),
          })
          .optional(),
        INSURED: z
          .object({
            price: z.number(),
            pricedTreatmentId: z.number().or(z.string()),
          })
          .optional(),
      }),
      mode: z.enum(["CASH", "INSURED"]),
    }),
  ),
});

type FetchPricingSuccess = z.infer<typeof FetchPricingSuccessSchema>;

/**
 * Error Schema
 */
const FetchPricingErrorSchema = z.object({
  code: z.enum(["VALIDATION_ERROR", "PRICING_FETCH_FAILED", "NETWORK_ERROR"]),
  message: z.string(),
  retryable: z.boolean(),
});

type FetchPricingError = z.infer<typeof FetchPricingErrorSchema>;

type Provider = {
  id: number;
  npiId: string;
  firstName: string;
  lastName: string;
  ehrId: string | null;
  degrees: string;
  __typename: "Provider";
};

type DefaultProvider = {
  npi: string;
  practiceId: number;
  orgId: number;
};
/**
 * Dependencies
 */
export interface FetchPricingDependencies {
  analytics?: {
    track: (event: string, properties?: Record<string, unknown>) => void;
  };
}

/**
 * Fetch Pricing Actor Implementation
 */
const fetchPricingActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: FetchPricingInput & { deps?: FetchPricingDependencies };
  }): Promise<FetchPricingSuccess> => {
    const {
      consumerId,
      firstName,
      lastName,
      dateOfBirth,
      insurances = [],
      hrtIds,
      orgId,
      email,
      insuranceVerified = false,
      deps,
      practiceId,
      isPendingPricesFetch,
    } = input;

    const fixtureId = getStateIdFromUrl();

    // Validate input
    const validated = FetchPricingInputSchema.safeParse({
      consumerId,
      firstName,
      lastName,
      dateOfBirth,
      insurances,
      hrtIds,
      orgId,
      email,
      insuranceVerified,
      practiceId,
      isPendingPricesFetch,
    });

    if (!validated.success) {
      const errorData: FetchPricingError = {
        code: "VALIDATION_ERROR",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    try {
      const consumer = {
        id: consumerId ?? "",
        fname: firstName,
        lname: lastName,
        dob: dateOfBirth,
      };

      // Only fetch insured prices if insurance is verified
      const hasVerifiedInsurance =
        insuranceVerified && insurances && insurances.length > 0;

      const insuredHrtIds =
        hasVerifiedInsurance || isPendingPricesFetch ? hrtIds : [];

      const cashHrtIds = hrtIds;

      const providersResult = await ResultAsync.fromPromise<Provider[], Error>(
        fetch(`/api/providers?orgId=${orgId}`).then(async (res) => {
          if (!res.ok) {
            throw new Error(`Failed to fetch providers: ${res.statusText}`);
          }
          return res.json();
        }),
        (error) =>
          new Error(
            error instanceof Error
              ? error.message
              : "Failed to fetch providers",
          ),
      );

      if (providersResult.isErr()) {
        throw providersResult.error;
      }

      const defaultProviderResult = await ResultAsync.fromPromise<
        DefaultProvider,
        Error
      >(
        fetch(`/api/default-providers?practiceId=${practiceId}`).then(
          async (res) => {
            if (!res.ok) {
              throw new Error(`Failed to fetch providers: ${res.statusText}`);
            }
            return res.json();
          },
        ),
        (error) =>
          new Error(
            error instanceof Error
              ? error.message
              : "Failed to fetch providers",
          ),
      );

      const providers = providersResult.isOk() ? providersResult.value : [];
      const defaultProviderValue = defaultProviderResult.isOk()
        ? defaultProviderResult.value
        : null;

      const defaultProvider = providers.find(
        (provider) =>
          provider.npiId ===
          (defaultProviderValue ? defaultProviderValue.npi : ""),
      );

      const pricingMap = await getKioskPrices(
        insuredHrtIds,
        cashHrtIds,
        consumer,
        insurances || [],
        orgId,
        email,
        firstName,
        lastName,
        dateOfBirth,
        practiceId,
        defaultProvider,
        isPendingPricesFetch,
        fixtureId,
      );

      return {
        pricingMap,
      };
    } catch (error) {
      const errorData: FetchPricingError = {
        code: "PRICING_FETCH_FAILED",
        message:
          error instanceof Error ? error.message : "Failed to fetch pricing",
        retryable: true,
      };
      const err = new Error(errorData.message);
      Object.assign(err, errorData);
      throw err;
    }
  },
);

/**
 * Fetch Pricing Actor with Metadata
 */
export const fetchPricingActor: ActorWithMetadata = {
  impl: fetchPricingActorImpl,
  metadata: {
    displayName: "fetchPricing",
    description: "Fetch treatment prices (cash and/or insured)",
    category: "data",
    retryable: true,
    timeout: 30000, // 30 seconds
    errorCodes: ["VALIDATION_ERROR", "PRICING_FETCH_FAILED", "NETWORK_ERROR"],
    inputSchema: FetchPricingInputSchema,
    successSchema: FetchPricingSuccessSchema,
    errorSchema: FetchPricingErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as FetchPricingSuccess;
      return {
        pricingMap: success.pricingMap,
        pricingError: undefined,
      };
    },
    onError: (error: unknown) => {
      const err = error as FetchPricingError;
      return {
        pricingError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
